// ================================================================================
// Custom events
//
// To help make it easier to add custom functionality, the theme emits custom
// events (along with their associated data) related to key theme features.
//
// To enable this functionality update the 'useCustomEvents' variable in
// the theme.liquid file to 'true'.
// ================================================================================

/* eslint-disable no-console */

// ================================================================================
// BUNDLE ONE-PER-ORDER RESTRICTION
// Tribal Sport — Eclipse Theme
//
// Fast Bundle bypasses the Eclipse native cart handler entirely
// (confirmed via x-fastbundle-request header + empty line item properties).
// So instead of relying on Eclipse events, we intercept window.fetch directly
// to catch any /cart/add.js or /cart/update.js calls from any app.
//
// NOTE: Multiple apps wrap window.fetch (MWS Fees, Fast Bundle, Eclipse).
// Our wrapper must properly re-throw errors to avoid breaking the fetch chain.
// ================================================================================

const RESTRICTED_BUNDLE_IDS = [
  8221778083887, // Ladies Bundle
  8221790732335, // Managers Bundle
  8187100069935, // Mens Bundle
];

const BUNDLE_NAMES = {
  8221778083887: 'Ladies Bundle',
  8221790732335: 'Managers Bundle',
  8187100069935: 'Mens Bundle',
};

const BUNDLE_HANDLES = [
  'laq-state-team-ladies-bundle',
  'laq-state-team-managers-bundle',
  'laq-state-team-mens-bundle',
];

let lastBundleCounts = {};
let isProcessing = false;

async function fetchCart() {
  const res = await fetch('/cart.js');
  return res.json();
}

function getBundleCounts(cartItems) {
  const counts = {};
  for (const id of RESTRICTED_BUNDLE_IDS) {
    counts[id] = cartItems.filter((item) => item.product_id === id).length;
  }
  return counts;
}

async function removeDuplicateBundle(cartItems, productId) {
  const updates = {};
  let firstSkipped = false;

  cartItems.forEach((item, index) => {
    if (item.product_id !== productId) return;
    if (!firstSkipped) {
      firstSkipped = true; // keep first occurrence
    } else {
      updates[index + 1] = 0; // zero out duplicates (1-based index)
    }
  });

  if (Object.keys(updates).length === 0) return;

  await fetch('/cart/update.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ updates }),
  });
}

// ================================================================================
// FIX: refreshMiniCart() previously dispatched a made-up event name
// ('apps:product-added-to-cart') that nothing in the theme listens for, so it
// never actually opened the cart drawer for Fast Bundle (or any app-driven)
// adds. The theme's real cart-drawer.js listens for EVENTS.CART_DRAWER_OPENING
// / EVENTS.CART_DRAWER_OPEN (window events) to activate + open itself, and
// EVENTS.CART_ITEM_ADDED (document event, 'cart:item-added') to scroll the
// drawer to the top. Dispatch all three so app-driven adds behave the same as
// native Eclipse adds.
// ================================================================================
function refreshMiniCart() {
  window.dispatchEvent(new Event('fluco:modal:cartDrawer:opening'));
  window.dispatchEvent(new Event('fluco:modal:cartDrawer:open'));
  document.dispatchEvent(new CustomEvent('cart:item-added'));
}

// ================================================================================
// FIX (v2): Fast Bundle's actual add-to-cart call never shows up as a browser
// fetch/XHR to /cart/add.js — it appears to be proxied through Fast Bundle's own
// backend (api.fastbundle.co), so our window.fetch intercept below never fires
// for it. To catch adds regardless of how they happen under the hood, we poll
// the cart item count and open the drawer whenever it increases.
// ================================================================================

let lastKnownCartCount = null;

async function pollCartForChanges() {
  try {
    const cart = await fetchCart();

    if (lastKnownCartCount !== null && cart.item_count > lastKnownCartCount) {
      refreshMiniCart();

      // FIX (v3): CartContent (the base class cart-drawer.js extends) only
      // actually re-fetches and swaps in the drawer's HTML content when it
      // sees a 'cart:changed' document event (see cart-content.js). We were
      // never dispatching that, so the drawer would open but stay empty
      // until a manual page refresh forced a fetch. Dispatch it here with
      // the freshly-fetched cart data so the drawer content updates too.
      document.dispatchEvent(
        new CustomEvent('cart:changed', { detail: { cartData: cart } }),
      );

      setTimeout(checkForDuplicateBundles, 600);
    }

    lastKnownCartCount = cart.item_count;
  } catch (_) {
    // Never break polling due to a transient fetch error
  }
}

// Establish baseline, then poll periodically for count increases
fetchCart()
  .then((cart) => {
    lastKnownCartCount = cart.item_count;
  })
  .catch(() => {})
  .finally(() => {
    setInterval(pollCartForChanges, 1500);
  });

function showBundleWarning(productId) {
  const name = BUNDLE_NAMES[productId] || 'this bundle';
  alert(
    `Only "${name}" is allowed per order.\n\nPlease complete your checkout before adding another.`
  );
}

async function checkForDuplicateBundles() {
  if (isProcessing) return;

  try {
    const cart = await fetchCart();
    const currentCounts = getBundleCounts(cart.items);

    for (const id of RESTRICTED_BUNDLE_IDS) {
      const current = currentCounts[id] || 0;
      const previous = lastBundleCounts[id] || 0;

      if (current > 1 && current > previous) {
        isProcessing = true;
        console.log(`[TS] Duplicate bundle detected: ${BUNDLE_NAMES[id]}`);
        await removeDuplicateBundle(cart.items, parseInt(id));
        showBundleWarning(parseInt(id));

        // Re-fetch to update counts after removal
        const updatedCart = await fetchCart();
        lastBundleCounts = getBundleCounts(updatedCart.items);

        // Refresh mini cart after duplicate removal
        refreshMiniCart();

        isProcessing = false;
        return; // handle one at a time
      }
    }

    lastBundleCounts = currentCounts;
  } catch (err) {
    console.error('[TS] Bundle check error:', err);
    isProcessing = false;
  }
}

// ================================================================================
// DUPLICATE ATC BUTTON HIDE
//
// On bundle product pages, both Easify (.rbr-bap-new-footer) and TPO/Fast Bundle
// (.tpo_add-to-cart) inject ATC buttons. Easify is the one that actually handles
// the cart add, so we hide the TPO button instead.
// TPO injects dynamically so we need a multi-layered approach to reliably hide it.
// ================================================================================

const currentHandle = window.location.pathname.split('/products/')[1]?.split('?')[0];

if (currentHandle && BUNDLE_HANDLES.includes(currentHandle)) {

  function hideDuplicateATCButton() {
    document.querySelectorAll('.tpo_add-to-cart').forEach((el) => {
      el.style.setProperty('display', 'none', 'important');
    });
  }

  // 1. Try immediately on script load
  hideDuplicateATCButton();

  // 2. Try on DOMContentLoaded
  document.addEventListener('DOMContentLoaded', hideDuplicateATCButton);

  // 3. MutationObserver — catches dynamic injection
  const observer = new MutationObserver(hideDuplicateATCButton);
  observer.observe(document.documentElement, { childList: true, subtree: true });

  // 4. Aggressive polling fallback — every 100ms for 5 seconds
  let attempts = 0;
  const interval = setInterval(() => {
    hideDuplicateATCButton();
    attempts++;
    if (attempts >= 50) clearInterval(interval); // stop after 5s
  }, 100);

  console.log('[TS] Duplicate ATC hide active for:', currentHandle);
}

// ================================================================================
// CORE: Intercept window.fetch
//
// Multiple apps wrap window.fetch (MWS Fees, Fast Bundle, Eclipse main).
// Our wrapper must:
//   1. Always re-throw fetch errors so the chain isn't broken
//   2. Only trigger our logic when the fetch actually succeeded (result.ok)
//   3. Refresh the mini cart after a successful bundle add
// ================================================================================

(function () {
  const _originalFetch = window.fetch;

  window.fetch = async function (...args) {
    let result;

    try {
      result = await _originalFetch.apply(this, args);
    } catch (err) {
      // Re-throw so other apps in the fetch chain aren't broken
      throw err;
    }

    try {
      const url = typeof args[0] === 'string'
        ? args[0]
        : args[0]?.url || '';

      const isCartAdd = url.includes('/cart/add');
      const isCartUpdate = url.includes('/cart/update');

      if ((isCartAdd || isCartUpdate) && result.ok) {
        // Refresh mini cart so items appear without needing a page reload
        setTimeout(refreshMiniCart, 800);

        // Check for duplicate bundles after cart settles
        setTimeout(checkForDuplicateBundles, 600);
      }
    } catch (_) {
      // Never break fetch behaviour due to our logic
    }

    return result;
  };
})();

// Initialize lastBundleCounts on page load so we have a baseline
fetchCart()
  .then((cart) => {
    lastBundleCounts = getBundleCounts(cart.items);
    console.log('[TS] Bundle restriction initialized', lastBundleCounts);
  })
  .catch(() => {});

// ================================================================================
// "Item added to the cart"
//
// - Fires whenever an item has been added to the cart
// - Exposed when the ajax cart is enabled
// - "detail" object data:
//   - The associated product object
// - NOTE: Fast Bundle bypasses this event, but we keep it as a fallback
//   in case other apps or native ATC flows do fire it.
// ================================================================================
document.addEventListener('cart:item-added', function (evt) {
  console.log('Item added to the cart');
  console.log(evt.detail.product);

  // Fallback refresh + duplicate check
  setTimeout(refreshMiniCart, 800);
  setTimeout(checkForDuplicateBundles, 600);
});

// ================================================================================
// "Cart changed"
//
// - Fires whenever the cart is changed
// - Exposed when the ajax cart is enabled
// - "detail" object data:
//   - The cart object
// ================================================================================
document.addEventListener('cart:changed', function (evt) {
  console.log('Cart changed');
  console.log(evt.detail.cart);
});

// ================================================================================
// "Cart error"
//
// - Fires whenever there is an error when adding an item to the cart
//   - Typically due to a product not having sufficient stock
// - "detail" object data:
//   - The error message
// ================================================================================
document.addEventListener('cart:error', function (evt) {
  console.log('Cart error');
  console.log(evt.detail.errorMessage);
});

// ================================================================================
// "Quick cart opened"
//
// - Fires whenever the quick cart is opened
// - Exposed when the ajax cart is enabled
// - "detail" object data:
//   - The cart object
// ================================================================================
document.addEventListener('quick-cart:open', function (evt) {
  console.log('Quick cart opened');
  console.log(evt.detail.cart);
});

// ================================================================================
// "Quick cart closed"
//
// - Fires whenever the quick cart is closed
// - Exposed when the ajax cart is enabled
// ================================================================================
document.addEventListener('quick-cart:close', function () {
  console.log('Quick cart closed');
});

// ================================================================================
// "Product variant changed"
//
// - Fires whenever a variant product is selected
// - Exposed when a 'Variant selectors' block has been added to a product template
//   or featured product section
// - "detail" object data:
//   - The variant object
// ================================================================================
document.addEventListener('product:variant:changing', function (evt) {
  console.log('Product variant changed');
  console.log(evt.detail.variant);
});

// ================================================================================
// "Product quantity updated"
//
// - Fires whenever a product quantity is updated
// - Exposed when a 'Quantity selector' block has been added to a product template
//   or featured product section
// - "detail" object data:
//   - The variant object
//   - The quantity
// ================================================================================
document.addEventListener('product:quantity-update', function (evt) {
  console.log('Product quantity updated');
  console.log(evt.detail.quantity, evt.detail.variant);
});

// ================================================================================
// "Quick view loaded"
//
// - Fires whenever a quick view modal is opened
// - Exposed when the 'Enable quick view' setting is enabled and a quick view
//   modal is opened
// ================================================================================
document.addEventListener('quick-view:loaded', function () {
  console.log('Quick view loaded');
});

/* eslint-enable no-console */

// ================================================================================
// SIMPLE BUNDLES — PAIRING GUARD + QUANTITY LOCK
// Tribal Sport — Eclipse Theme
//
// Simple Bundles' dropdown-style "BYOB" bundles (Package 1-4, table tennis
// packages, etc.) let customers pick a "Pairing" (e.g. "Competition Tee +
// Competition Polo") plus one variant select per component product. Two gaps
// in the native widget:
//   1. All component selects stay visible/required even when a component
//      isn't part of the chosen pairing — customer could fill in a size for
//      a product that won't actually be added.
//   2. The bundle's quantity stepper (how many bundle packs) appears once a
//      variant is selected and is easy to mistake for a per-item quantity,
//      so we lock it to 1.
//
// This is independent of the Fast Bundle restriction logic above — different
// app, DOM-based only, no fetch interception, no cart polling. Auto-detects
// the pairing select (the one whose option values contain " + ") and matches
// it to other selects by label text, so it works across Package 1-4 without
// hardcoding product names. Scoped to only run where Simple Bundles' dropdown
// widget is present, so it's safe to leave in this shared file.
// ================================================================================

(function () {
  const GUARD_ID = '__tsBundleGuard';
  if (window[GUARD_ID]) return;

  // Simple Bundles injects #simple-bundles-io-options asynchronously after
  // its own app script runs, so it isn't guaranteed to exist yet by the time
  // this deferred module executes. Poll for it instead of a single check,
  // matching the retry pattern already used above for the TPO ATC button hide.
  let bundleInitAttempts = 0;
  const bundleInitPoll = setInterval(() => {
    bundleInitAttempts++;
    if (document.getElementById('simple-bundles-io-options')) {
      clearInterval(bundleInitPoll);
      runBundleGuard();
    } else if (bundleInitAttempts >= 50) {
      clearInterval(bundleInitPoll); // give up after ~5s, not a bundle page
    }
  }, 100);

  function normalize(s) {
    return (s || '').trim().toLowerCase();
  }

  // A "controller" select is one whose non-empty option values — split on
  // " + " for multi-part pairings like "Competition Tee + Competition Polo",
  // or taken whole for single-choice controllers like "Shirt" (Package 2's
  // "pick 1 of 3 + fixed Track Jacket" style) — all match the label of some
  // other select on the page. This covers both bundle shapes with one
  // detector, no per-package hardcoding.
  function findControllerSelect() {
    const selects = Array.from(document.querySelectorAll('select'));
    const labelSet = new Set(
      selects.map((sel) => {
        const label = document.querySelector(`label[for="${sel.id}"]`);
        return normalize(label ? label.textContent : sel.name);
      })
    );
    return selects.find((sel) => {
      const options = Array.from(sel.options).filter((o) => o.value);
      if (options.length === 0) return false;
      return options.every((o) =>
        o.value.split(' + ').map(normalize).every((p) => labelSet.has(p))
      );
    });
  }

  // Only maps fields the controller actually references. A fixed/mandatory
  // field (e.g. Track Jacket in Package 2) is never mentioned in the
  // controller's option values, so it's excluded here and left completely
  // untouched by toggleField — it stays visible and required exactly as
  // Simple Bundles rendered it.
  function buildFieldMap(controllerSelect) {
    const referencedNames = new Set();
    Array.from(controllerSelect.options).forEach((o) => {
      if (!o.value) return;
      o.value.split(' + ').forEach((p) => referencedNames.add(normalize(p)));
    });

    const otherSelects = Array.from(document.querySelectorAll('select')).filter(
      (s) => s !== controllerSelect
    );
    const map = {};
    otherSelects.forEach((sel) => {
      const label = document.querySelector(`label[for="${sel.id}"]`);
      const name = normalize(label ? label.textContent : sel.name);
      if (name && referencedNames.has(name)) map[name] = sel.id;
    });
    return map;
  }

  function toggleField(id, show) {
    const select = document.getElementById(id);
    const label = document.querySelector(`label[for="${id}"]`);
    if (!select) return;
    select.style.display = show ? '' : 'none';
    if (label) label.style.display = show ? '' : 'none';
    select.disabled = !show;
    select.required = show;
    if (!show) select.value = '';
  }

  // NOTE: this runs on every tick of the shared interval below, not just
  // once on init. Simple Bundles populates the pairing select's options
  // (and sometimes its default value) asynchronously — sometimes after our
  // container-existence check already fired — so a single-shot check can
  // permanently miss it. Re-deriving every tick makes this self-healing:
  // it doesn't matter when the options actually finish loading, or whether
  // the app sets an initial value via JS without firing a 'change' event.
  function syncPairing() {
    const controllerSelect = findControllerSelect();
    if (!controllerSelect) return; // no controller field on this package

    const fieldMap = buildFieldMap(controllerSelect);
    const parts = controllerSelect.value.split(' + ').map(normalize);
    Object.entries(fieldMap).forEach(([name, id]) => {
      toggleField(id, parts.includes(name));
    });

    // Attach a 'change' listener once for snappier response to real user
    // interaction; the interval below is the safety net for everything else.
    if (!controllerSelect.dataset.tsListenerAttached) {
      controllerSelect.dataset.tsListenerAttached = 'true';
      controllerSelect.addEventListener('change', syncPairing);
    }
  }

  function lockQuantity() {
    const qtyInput = document.querySelector('quantity-input input[name="quantity"]');
    const qtyButtons = document.querySelectorAll('quantity-input button');
    if (qtyInput && qtyInput.value !== '1') qtyInput.value = '1';
    if (qtyInput) {
      qtyInput.setAttribute('readonly', 'readonly');
      qtyInput.style.pointerEvents = 'none';
      qtyInput.style.opacity = '0.6';
    }
    qtyButtons.forEach((btn) => {
      btn.disabled = true;
      btn.style.pointerEvents = 'none';
      btn.style.opacity = '0.4';
    });
  }

  function syncAll() {
    syncPairing();
    lockQuantity();
  }

  function runBundleGuard() {
    syncAll();

    if (window.__tsSyncInterval) clearInterval(window.__tsSyncInterval);
    window.__tsSyncInterval = setInterval(syncAll, 500);

    window[GUARD_ID] = true;
  }
})();